import {
  ENUM_COLUMN_TYPE,
  ISO_DATE_PATTERN,
  PAIRING_TYPE_DIFFERENT,
  PAIRING_TYPE_SAME,
  TYPE_FAMILIES,
} from "../types/constants";
import { isEmpty } from "./expressionShapeUtils";
import { getReportSourcePrefix } from "config";

const nonEmpty = (list) => Array.isArray(list) && list.length > 0;

/** Maps each BINARY and SPECIAL function to its pairing type. */
export const buildFuncPairingType = (data) =>
  Object.fromEntries(
    [...(data?.BINARY ?? []), ...(data?.SPECIAL ?? [])]
      .filter((e) => e.pairingType)
      .map((e) => [e.func, e.pairingType])
  );

/** Maps each function with a non-empty enumerations list to that list. */
export const buildFuncEnumerations = (data) =>
  Object.fromEntries(
    Object.values(data ?? {})
      .flat()
      .filter((e) => nonEmpty(e?.enumerations))
      .map((e) => [e.func, e.enumerations])
  );

/** Returns the TYPE_FAMILIES names whose field types intersect types. */
export const typeFamilies = (types) =>
  Object.entries(TYPE_FAMILIES)
    .filter(([, members]) => members.some((t) => (types ?? []).includes(t)))
    .map(([family]) => family);

const literalTypes = (exp) => {
  if (typeof exp === "number") return ["INT", "FLOAT"];
  if (typeof exp === "boolean") return ["BOOL"];
  return ISO_DATE_PATTERN.test(exp) ? ["STRING", "DATE"] : ["STRING"];
};

const firstFilled = (operands) => operands.find((o) => !isEmpty(o));

const caseBranches = (exp) => [
  ...(exp.cases ?? []).map((c) => c.then),
  exp.otherwise,
];

const firstFilledTypes = (operands, context) => {
  const filled = firstFilled(operands);
  return filled === undefined ? null : operandTypes(filled, context);
};

/** Returns the field types a filled operand can yield, or null. */
export const operandTypes = (exp, context) => {
  if (isEmpty(exp)) return null;
  if (typeof exp !== "object") return literalTypes(exp);
  const { funcProduces = {}, availableColumns = [] } = context ?? {};
  if (exp.func === "VAR") {
    const column = availableColumns.find((c) => c.internalName === exp.name);
    return column?.type ? [column.type] : null;
  }
  if (exp.func === "IN") return ["BOOL"];
  if (exp.func === "ALL_OR_NONE")
    return firstFilledTypes(exp.values ?? [], context);
  if (exp.func === "CASE") return firstFilledTypes(caseBranches(exp), context);
  return nonEmpty(funcProduces[exp.func]) ? funcProduces[exp.func] : null;
};

const sameTypes = (validFor, anchorFamilies) => {
  const types = anchorFamilies.flatMap((f) => TYPE_FAMILIES[f]);
  return nonEmpty(validFor) ? types.filter((t) => validFor.includes(t)) : types;
};

const differentTypes = (validFor, anchorFamilies) =>
  validFor.filter(
    (t) => !typeFamilies([t]).some((f) => anchorFamilies.includes(f))
  );

/** Returns the field types an empty operand paired with anchorTypes may accept, or null. */
export const pairedAcceptedTypes = (pairingType, validFor, anchorTypes) => {
  if (!nonEmpty(anchorTypes)) return null;
  const allowed = validFor ?? [];
  const anchorFamilies = typeFamilies(anchorTypes);
  let result;
  if (pairingType === PAIRING_TYPE_SAME)
    result = sameTypes(allowed, anchorFamilies);
  else if (pairingType === PAIRING_TYPE_DIFFERENT) {
    if (!nonEmpty(allowed)) return null;
    result = differentTypes(allowed, anchorFamilies);
  } else return null;
  if (result.length > 0) return result;
  return nonEmpty(allowed) ? allowed : null;
};

const internalNames = (list) => new Set(list.map((e) => e?.internalName));

/** Returns true when both lists hold the same set of internalName values. */
export const enumerationsMatch = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  const namesA = internalNames(a);
  const namesB = internalNames(b);
  return namesA.size === namesB.size && [...namesA].every((n) => namesB.has(n));
};

/** Returns the non-empty enumeration list a filled operand yields, or null. */
export const anchorEnumerations = (exp, context) => {
  if (isEmpty(exp) || typeof exp !== "object") return null;
  const { columnEnumerations = {}, funcEnumerations = {} } = context ?? {};
  if (exp.func === "VAR") {
    const list = columnEnumerations[exp.name];
    return nonEmpty(list) ? list : null;
  }
  if (exp.func === "ALL_OR_NONE")
    return firstEnumerations(exp.values ?? [], context);
  if (exp.func === "CASE") return firstEnumerations(caseBranches(exp), context);
  const list = funcEnumerations[exp.func];
  return nonEmpty(list) ? list : null;
};

const firstEnumerations = (operands, context) => {
  for (const operand of operands) {
    const list = anchorEnumerations(operand, context);
    if (list) return list;
  }
  return null;
};

const borrowEnumerations = (constraints, operand, enumerations, context) =>
  enumerations && !anchorEnumerations(operand, context)
    ? { ...constraints, requiredEnumerations: enumerations }
    : constraints;

/** Returns the constraints for each operand of a pass-through function. */
export const passThroughConstraints = (
  operands,
  slotAcceptedTypes,
  slotEnumerations,
  context
) => {
  const slot = {
    acceptedTypes: slotAcceptedTypes ?? [],
    requiredEnumerations: slotEnumerations,
  };
  const anchorIndex = operands.findIndex((o) => !isEmpty(o));
  if (anchorIndex === -1) return operands.map(() => slot);
  const anchor = operands[anchorIndex];
  const enumerations = firstEnumerations(operands, context);
  const paired = {
    acceptedTypes:
      pairedAcceptedTypes(
        PAIRING_TYPE_SAME,
        slotAcceptedTypes,
        operandTypes(anchor, context)
      ) ??
      slotAcceptedTypes ??
      [],
    requiredEnumerations: enumerations ?? slotEnumerations,
  };
  const anchorSlot = slotEnumerations
    ? slot
    : borrowEnumerations(slot, anchor, enumerations, context);
  return operands.map((_, i) => (i === anchorIndex ? anchorSlot : paired));
};

const pairedTypes = (anchor, { pairingType, validFor }, context) =>
  pairedAcceptedTypes(pairingType, validFor, operandTypes(anchor, context)) ??
  validFor;

const pairedConstraints = (anchor, pairing, context) => {
  const constraints = { acceptedTypes: pairedTypes(anchor, pairing, context) };
  if (pairing.pairingType === PAIRING_TYPE_SAME)
    constraints.requiredEnumerations = anchorEnumerations(anchor, context);
  return constraints;
};

/** Returns the constraints for the two operands of a paired function. */
export const pairOperandConstraints = (a, b, pairing, context) => {
  const plain = { acceptedTypes: pairing.validFor };
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty && bEmpty) return { a: plain, b: plain };
  if (!aEmpty && !bEmpty) {
    const aTyped = { acceptedTypes: pairedTypes(b, pairing, context) };
    const bTyped = { acceptedTypes: pairedTypes(a, pairing, context) };
    if (pairing.pairingType !== PAIRING_TYPE_SAME)
      return { a: aTyped, b: bTyped };
    return {
      a: borrowEnumerations(aTyped, a, anchorEnumerations(b, context), context),
      b: borrowEnumerations(bTyped, b, anchorEnumerations(a, context), context),
    };
  }
  return aEmpty
    ? { a: pairedConstraints(b, pairing, context), b: plain }
    : { a: plain, b: pairedConstraints(a, pairing, context) };
};

/** Returns the constraints for the value and each param of an IN function. */
export const inOperandConstraints = (value, params, pairing, context) => {
  const operands = [value, ...params];
  const plain = { acceptedTypes: pairing.validFor };
  if (firstFilled(operands) === undefined)
    return { value: plain, params: params.map(() => plain) };
  const enumerations =
    pairing.pairingType === PAIRING_TYPE_SAME
      ? firstEnumerations(operands, context)
      : null;
  const forSlot = (slot, index) => {
    const anchor = operands.find((o, i) => i !== index && !isEmpty(o));
    if (!isEmpty(slot)) {
      const typed = anchor
        ? { acceptedTypes: pairedTypes(anchor, pairing, context) }
        : plain;
      return borrowEnumerations(typed, slot, enumerations, context);
    }
    const anchored = pairedConstraints(anchor, pairing, context);
    return enumerations && !anchored.requiredEnumerations
      ? { ...anchored, requiredEnumerations: enumerations }
      : anchored;
  };
  return {
    value: forSlot(value, 0),
    params: params.map((p, i) => forSlot(p, i + 1)),
  };
};

/** Returns true when a dragged column may be dropped into a slot. */
export const columnDropAllowed = (
  item,
  acceptedTypes,
  requiredEnumerations,
  columnEnumerations
) => {
  if (nonEmpty(requiredEnumerations))
    return (
      item.type === ENUM_COLUMN_TYPE &&
      enumerationsMatch(
        columnEnumerations?.[item.internalName],
        requiredEnumerations
      )
    );
  return !acceptedTypes?.length || acceptedTypes.includes(item.type);
};

/** Returns true when a function may be offered for a slot's required enumerations. */
export const enumFunctionAllowed = (func, requiredEnumerations, context) => {
  const { funcProduces = {}, funcEnumerations = {} } = context ?? {};
  return (
    !nonEmpty(requiredEnumerations) ||
    !(funcProduces[func] ?? []).includes(ENUM_COLUMN_TYPE) ||
    enumerationsMatch(funcEnumerations[func], requiredEnumerations)
  );
};

/** True when sourceTable names a report (saved or duplicated) rather than a native table. */
export const isReportSource = (sourceTable) =>
  typeof sourceTable === "string" &&
  sourceTable.startsWith(getReportSourcePrefix());

/** Groups ENUM columns from native-table sources into [{ tableName, columnNames }]. */
export const enumColumnTables = (availableColumns) => {
  const byTable = new Map();
  (availableColumns ?? [])
    .filter(
      (c) =>
        c?.type === ENUM_COLUMN_TYPE &&
        c.sourceTable &&
        !isReportSource(c.sourceTable)
    )
    .forEach((c) => {
      if (!byTable.has(c.sourceTable)) byTable.set(c.sourceTable, []);
      byTable.get(c.sourceTable).push(c.internalName);
    });
  return [...byTable].map(([tableName, columnNames]) => ({
    tableName,
    columnNames,
  }));
};

/** Maps each report-sourced column's internalName to its non-empty original.type.enumerations. */
export const reportColumnEnumerations = (availableColumns) =>
  Object.fromEntries(
    (availableColumns ?? [])
      .filter(
        (c) =>
          isReportSource(c?.sourceTable) &&
          nonEmpty(c.original?.type?.enumerations)
      )
      .map((c) => [c.internalName, c.original.type.enumerations])
  );

/** Maps each column descriptor's internalName to its non-empty enumerations. */
export const enumerationsByColumn = (data) =>
  Object.fromEntries(
    (data ?? [])
      .filter((d) => nonEmpty(d?.enumerations))
      .map((d) => [d.internalName, d.enumerations])
  );

/** Returns the intersection of slotTypes and validFor when both are non-empty, else whichever is non-empty, else []. */
export const intersectSlotTypes = (slotTypes, validFor) => {
  if (nonEmpty(slotTypes) && nonEmpty(validFor))
    return slotTypes.filter((t) => validFor.includes(t));
  if (nonEmpty(slotTypes)) return slotTypes;
  return nonEmpty(validFor) ? validFor : [];
};

/** Returns the displayName of the enumeration whose internalName is value, or null. */
export const enumerationLabel = (value, enumerations) => {
  if (!nonEmpty(enumerations)) return null;
  const match = enumerations.find((e) => e?.internalName === value);
  return match ? match.displayName ?? String(match.internalName) : null;
};
